import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, ShoppingCartSimple, Lock } from "@phosphor-icons/react";

// Electrical foreground image (cables / power)
const HERO_IMG =
  "https://images.unsplash.com/photo-1650698877967-734f036a0c08?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
// Offshore rig moved to a subtle back layer
const OFFSHORE_BACK =
  "https://images.unsplash.com/photo-1563118351-26b2c7381731?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600";

const LINES = ["Electrical", "Supply.", "Wireline", "Services."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.11 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const backY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-background"
      data-testid="hero-section"
    >
      {/* BACK LAYER: subtle offshore rig — heritage, not focus */}
      <motion.div
        style={{ y: backY }}
        className="absolute inset-0 z-0 opacity-20"
        aria-hidden="true"
      >
        <img
          src={OFFSHORE_BACK}
          alt=""
          className="h-full w-full object-cover grayscale contrast-125"
        />
        <div className="absolute inset-0 bg-background/60 mix-blend-multiply" />
      </motion.div>

      {/* FRONT LAYER: electrical/cables image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-[1] will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Electrical cables and industrial wiring"
          className="h-full w-full object-cover contrast-[1.08] saturate-[0.85]"
        />
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-background"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/25 to-background/55" />
      </motion.div>

      {/* Skeleton grid */}
      <div className="absolute inset-0 pointer-events-none z-[2]">
        <div className="mx-auto max-w-[1600px] h-full px-5 md:px-10 relative">
          <div className="absolute left-5 md:left-10 top-0 bottom-0 w-px bg-white/10" />
          <div className="absolute right-5 md:right-10 top-0 bottom-0 w-px bg-white/10" />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10 min-h-[100svh] flex flex-col justify-end pb-16 md:pb-24 pt-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="flex flex-wrap items-center gap-4 mb-8"
        >
          <span className="mono-label text-primary">/ Pendleton, SC</span>
          <span className="h-px w-16 bg-white/20" />
          <span className="mono-label text-muted-foreground">
            Cables · Transformers · Switchgear · Motors · Wireline
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.82] text-[15vw] md:text-[10vw] lg:text-[8vw] text-foreground">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i % 2 === 1 ? "text-primary" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="max-w-md text-base md:text-lg text-foreground/70 leading-relaxed"
          >
            Order industrial electrical supply — cables, transformers, switchgear, motors
            and safety devices — shipped from Pendleton, SC. Wireline services on request.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link
              to="/inventory"
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-4 mono-label hover:bg-foreground hover:text-background transition-colors duration-200"
              data-testid="hero-cta-shop"
            >
              <ShoppingCartSimple size={18} weight="bold" />
              Use Our Inventory
              <ArrowUpRight
                size={18}
                weight="bold"
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </Link>
            <a
              href="#contact"
              className="inline-flex items-center gap-3 border border-white/20 text-foreground px-7 py-4 mono-label hover:border-primary hover:text-primary transition-colors duration-200"
              data-testid="hero-cta-quote"
            >
              Request a Quote
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-12 flex flex-wrap items-center gap-6 text-muted-foreground"
        >
          <span className="flex items-center gap-2">
            <Lock size={14} weight="fill" className="text-primary" />
            <span className="mono-label text-[0.65rem]">HTTPS · Secure Checkout</span>
          </span>
          <span className="mono-label text-[0.65rem]">
            100+ SKUs stocked
          </span>
          <span className="mono-label text-[0.65rem]">
            HSE-certified wireline crews
          </span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.8 }}
        className="absolute bottom-6 right-5 md:right-10 hidden md:flex items-center gap-2 text-muted-foreground z-10"
      >
        <span className="mono-label">Scroll</span>
        <ArrowDown size={16} weight="light" className="animate-bounce" />
      </motion.div>
    </section>
  );
}
